import { KeyValuePipe } from '@angular/common'
import { Component, Input, inject } from '@angular/core'
import { FeatureGeoJSON, GeojsonPathElement } from '@core/models/geojson-edition.model'
import { GeojsonEditorService } from '@core/services/geojson-editor.service'

@Component({
  selector: 'app-editable-array-or-object',
  standalone: true,
  imports: [KeyValuePipe],
  templateUrl: './editable-array-or-object.component.html',
  styleUrl: './editable-array-or-object.component.scss',
})
export class EditableArrayOrObjectComponent {
  @Input() value: any
  @Input() path: GeojsonPathElement[] = []
  @Input() feature: any
  @Input() isEditable: boolean = false

  private readonly _geojsonEditor = inject(GeojsonEditorService)
  maxItems = 100 // In some case, like timeseries, the editable pop-up becomes too big and crashes the front. This is a safety net.

  isArray(val: any): val is any[] {
    return Array.isArray(val)
  }

  isObject(val: any): val is Record<string, unknown> {
    return val !== null && typeof val === 'object' && !Array.isArray(val)
  }

  isTooBigToDisplay(val: any): boolean {
    if (this.isArray(val)) {
      return val.length > this.maxItems
    }
    if (this.isObject(val)) {
      return Object.keys(val).length > this.maxItems
    }
    return false
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLElement).blur()
    }
  }

  onPropertyEdit(feat: FeatureGeoJSON, event: FocusEvent) {
    const newValueRaw = (event.target as HTMLElement).innerText

    const geojson = this._geojsonEditor.geojson$()
    if (!geojson) return
    const idx = feat.__geojsonIndex
    if (typeof idx === 'number' && geojson.features[idx]) {
      const originalProps = this._geojsonEditor.originalGeojson?.features?.[idx]?.properties
      const originalValue = this.getValueByPath(originalProps, this.path)

      // Type coercion
      let newValue: any = newValueRaw
      if (typeof originalValue === 'number') {
        const parsed = parseFloat(newValueRaw)
        newValue = isNaN(parsed) ? newValueRaw : parsed
      } else if (typeof originalValue === 'boolean') {
        const lower = newValueRaw.trim().toLowerCase()
        if (lower === 'true') newValue = true
        else if (lower === 'false') newValue = false
      }

      // Set the value at the path
      const props = geojson.features[idx].properties
      this.setValueByPath(props, this.path, newValue)
      this._geojsonEditor.geojson$.set({ ...geojson })
      this._geojsonEditor.lastClosedPopup$.set(idx)
    } else {
      console.warn('Feature index not found on feat', feat)
    }
  }

  getValueByPath(obj: any, path: GeojsonPathElement[]): any {
    return path.reduce((acc, key) => {
      if (acc == null) return undefined
      if (this._geojsonEditor.isArrayIndex(key)) {
        return acc[key.index]
      }
      return acc[key as string | number]
    }, obj)
  }

  setValueByPath(obj: any, path: GeojsonPathElement[], value: any): void {
    if (!obj || !path.length) return
    const lastKey = path[path.length - 1]
    const parent = path.slice(0, -1).reduce((acc, key) => {
      if (acc == null) return undefined
      if (this._geojsonEditor.isArrayIndex(key)) {
        return acc[key.index]
      }
      return acc[key as string | number]
    }, obj)
    if (parent != null) {
      if (this._geojsonEditor.isArrayIndex(lastKey)) {
        parent[lastKey.index] = value
      } else {
        parent[lastKey as string | number] = value
      }
    }
  }

  getPathWith(key: GeojsonPathElement) {
    return [...this.path, key]
  }
}
